// İSG uzmanı portalı — durum/uygunluk ton yardımcıları (Taşeron uygunluk / durum / belge
// geçerliliği). Token'lı; literal renk yok. Ton eşlemesi api-contract §13 wire değerlerinden
// türetilir — çeviri (etiket) çağıran katmandadır.
//
// **S33 Carbon Slice 4.** Eski app-local `Badge` bileşeni (5 tonlu palet: success/warning/
// danger/neutral/accent) KALDIRILDI — ekranlar artık paylaşılan `Tag` (plan §5.1, 4 renk:
// blue/red/green/gray) kullanıyor. Bu dosyanın işi DEĞİŞMEDİ, yalnız render taşındı: burada
// yalnız ton TÜRETİMİ (durum → renk) kalır, §13 iş kuralı KORUNUR. Eşleme plan §5.1'in
// "settled" kararına göre daraltıldı: `Pending`/`Expiring` → gray ("sürüyor, kimse suçlu
// değil"), `Suspended`/`Missing` → red (işi engeller), `accent`/`Yetkili` → blue (turuncu
// Carbon'da yalnız logoda kalır).

import {
  type ComplianceStatus,
  type ContractorAccessLevel,
  type ContractorPersonnelDto,
  type ContractorPersonnelStatus,
  type ContractorStatus,
  type PersonnelDocumentApprovalStatus,
  type PersonnelStatusKey,
  type SecurityPermitDenyReason,
  type SecurityTokenDenyReason,
  type TagColor,
  personnelStatusKey,
} from '@isgportal/shared';

const STATUS_LABEL_KEYS: Record<PersonnelStatusKey, string> = {
  active: 'contractors.personnel.status.active',
  inactive: 'contractors.personnel.status.inactive',
  notactivated: 'contractors.personnel.status.notactivated',
  pending: 'contractors.personnel.status.pending',
  expired: 'contractors.personnel.status.expired',
};

/** Durum etiketi anahtarı: `NoLogin` + aktif → "Etkinleştirilmedi", `NoLogin` + pasif → "Pasif" (GL6 §4.1). */
export function personnelStatusLabelKey(
  row: Pick<ContractorPersonnelDto, 'status' | 'isActive'>,
): string {
  return STATUS_LABEL_KEYS[personnelStatusKey(row)];
}

/**
 * Uygunluk / belge geçerliliği tonu (§13 türetilmiş durum). Öncelik en kötüden iyiye:
 * `Expired`/`Missing` (red) > `Expiring` (gray) > `Valid` (green).
 */
export function complianceTone(status: ComplianceStatus): TagColor {
  switch (status) {
    case 'Valid':
      return 'green';
    case 'Expiring':
      return 'gray';
    default:
      return 'red'; // Expired | Missing
  }
}

/** Taşeron durumu tonu (§13): `Active` yeşil, `Suspended` kırmızı (işi engeller), `Inactive` gri. */
export function contractorStatusTone(status: ContractorStatus): TagColor {
  switch (status) {
    case 'Active':
      return 'green';
    case 'Suspended':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Personel/davet durumu tonu (§13.3): `Active` yeşil, `NoLogin` gri (S37 design review M1:
 * `status` türetilmiş + kayıplı — `NoLogin` `IsActive=false`'u da gizler, api-contract.md:1735),
 * `Pending` gri (davet bekliyor), `Inactive`/`Expired` gri (pasif / süresi dolmuş — pasif durumlar).
 */
export function personnelStatusTone(status: ContractorPersonnelStatus): TagColor {
  switch (status) {
    case 'Active':
      return 'green';
    default:
      return 'gray'; // NoLogin (lossy status, api-contract.md:1735) | Pending | Inactive | Expired
  }
}

/**
 * Erişim seviyesi tonu (§13.3): `Yetkili` mavi (web portalı — yükseltilmiş erişim, eski
 * turuncu "accent"in göç hedefi), `Personeli` gri (varsayılan — mobil).
 */
export function accessLevelTone(level: ContractorAccessLevel): TagColor {
  return level === 'Yetkili' ? 'blue' : 'gray';
}

/**
 * Saha geçişi ret sebebi tonu (§29.1, S32) — İZİN ailesi (`NoPermit`/`OutOfSite`/
 * `EntryNotPermitted`/`OutOfValidity`/`Terminal`) her zaman `red`: hepsi PERSONELİ kapsayan
 * gerçek bir ret, "daha az kötü" bir alt kümesi yok. JETON ailesi (`TokenInvalid`/
 * `TokenExpired`/`TokenConsumed`) BİLEREK `gray` — bunlar kodun/panelin hâlidir, kişi-şekilli
 * bir ret DEĞİLDİR (S32 tasarım spec'i §5.2: jeton hâli sahnede asla bir ret gibi RENKLENMEZ).
 * Bilinmeyen/gelecekte eklenecek bir kod da `gray` (izin ifadesine ASLA düşmez — §29.1 kırıcı-
 * değişiklik notu).
 */
export function entryReasonTone(
  reason: SecurityPermitDenyReason | SecurityTokenDenyReason | string | null,
): TagColor {
  switch (reason) {
    case 'NoPermit':
    case 'OutOfSite':
    case 'EntryNotPermitted':
    case 'OutOfValidity':
    case 'Terminal':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Personel belgesi onay durumu tonu (§15.4): `Approved` yeşil (onaylı), `Rejected` kırmızı
 * (reddedildi — red gerekçesi ayrıca gösterilir), `Pending` gri (İSG uzmanı onayı bekliyor).
 */
export function personnelDocApprovalTone(status: PersonnelDocumentApprovalStatus): TagColor {
  switch (status) {
    case 'Approved':
      return 'green';
    case 'Rejected':
      return 'red';
    default:
      return 'gray';
  }
}
